import {
    SimplePresentationRunner,
    SimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {gsap} from "gsap";
import {Game} from "../Game";
import {SymbolContainer} from "../reels/SymbolContainer";
import {GameUtils} from "../utils/GameUtils";
import {Symbol} from "../reels/Symbol";
import {SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {Config} from "../config/Config";
import {Mental2SymbolStates, SYMBOL} from "../enums/Enum";
import {Sound} from "../sound/Sound";

export class EnhancerRunner implements SimplePresentationRunner {
    shouldRun(): boolean {
        return Game.model.fireFrameCount >= 4;
    }

    start(flowStateController: SimplePresentationRunnerController): void {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.add(() => {
            flowStateController.setSkipCallback(() => {
                this.onSkip(tl);
            });
        });

        if (Game.model.fireFrameCount >= 4) {
            tl.add(this.getEnhancerOpenAction(1, 2, Game.model.enhancerFeatures[0]), 0);
        }

        if (Game.model.fireFrameCount >= 8) {
            tl.add(this.getEnhancerOpenAction(2, 3, Game.model.enhancerFeatures[1]), 0);
        }

        if (Game.model.fireFrameCount >= 6) {
            tl.add(this.getEnhancerOpenAction(3, 2, Game.model.enhancerFeatures[2]), 0);
        }


        tl.add(() => {
            this.onComplete(flowStateController);
        })
    }

    public resizeReels(reelId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        let symWithAllSplits: Symbol[] = [];
        const isActiveXHolePresent: boolean = Game.model.isXHoleActive() && reelId === 2;
        Game.reelView.reelModels[reelId].getAllVisibleStopSymbols().forEach((symData: SimpleReelModelSymbolData) => {
            const symContainer: SymbolContainer = (<SymbolContainer>symData.positionable);
            symContainer.symbols.forEach((sym: Symbol) => {
                symWithAllSplits.push(sym);
            });
        });
        let totalCount: number = symWithAllSplits.length;
        let availableHeight: number = Config.REEL_HEIGHT[reelId] - 99;//isSpecialSymbolReel ? isEnhancer ? 198 * 2.5 : 198 * 2 : 198 * 3;

        if (isActiveXHolePresent) {
            availableHeight = availableHeight - 99;
            totalCount = totalCount - 1;
        }
        const eachSymbolHeight: number = availableHeight / totalCount;
        let incrementalY: number = 0;
        for (let i: number = 0; i < symWithAllSplits.length; i++) {
            const symbol: Symbol = symWithAllSplits[i];

            let height: number = eachSymbolHeight;
            if (isActiveXHolePresent && symbol.symbolName === SYMBOL.X_HOLE) {
                height = 99;
            }

            const actualHeight: number = Config.AVAILABLE_SYMBOL_SIZES.reduce((prev: number, curr: number) => {
                return (Math.abs(curr - height) < Math.abs(prev - height) ? curr : prev);
            });
            let state: Mental2SymbolStates = GameUtils.getReSizeState(actualHeight);
            if (actualHeight !== height) {
                tl.add(() => {
                    symbol.scale.y = height / actualHeight;
                });
            }
            let parentY: number = symbol.parent.position.y;
            let y: number = incrementalY - parentY - (99 - (height * 0.5));
            if (reelId === 2) {
                y += 99;
            }
            incrementalY = incrementalY + height;
            tl.add(() => {
                symbol.setState(state);
                symbol.currentHeight = height;
                symbol.onResize();
            }, 0);
            tl.to(symbol, {duration: 0.1, y: y}, 0);
        }
        return tl;
    }

    private onComplete(flowStateController: SimplePresentationRunnerController): void {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        Game.model.enhancerFeatures.forEach((symbolName: SYMBOL, index: number) => {
            if (symbolName !== SYMBOL.BLANK && symbolName !== SYMBOL.X_SPLIT && symbolName !== SYMBOL.X_WAYS && symbolName !== SYMBOL.X_NUDGE) {
                const reelId: number = index + 1;
                const rowId: number = reelId === 2 ? 3 : 2;
                const symbolContainer: SymbolContainer = GameUtils.getSymbol(reelId, rowId);
                tl.add(Game.stickyView.checkAndSetStickyDeadMultiplierToSymbol(symbolContainer), 0);
            }
        });

        tl.add(() => {
            flowStateController.finish();
        });
    }

    private getEnhancerOpenAction(reelId: number, rowId: number, triggeredFeature: SYMBOL): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (Game.model.data.evaluatedArea[reelId][rowId] != "BLANK") {
            Game.foregroundElements.waysCounter.symCountInReels[reelId]++;
            const symbolContainer: SymbolContainer = GameUtils.getSymbol(reelId, rowId);
            switch (triggeredFeature) {
                case SYMBOL.X_WAYS:
                case SYMBOL.X_SPLIT:
                    tl.add(symbolContainer.showEnhancerFeature(2, triggeredFeature), 0);
                    tl.add(Game.enhancerView.open(reelId), 0.2);
                    tl.add([
                        this.resizeReels(reelId),
                        symbolContainer.expandFeature(),
                        () => {
                            if (triggeredFeature === SYMBOL.X_WAYS) {
                                Game.soundPlayer.avoidFrequentPlaySoundAndPlayLastQueued(Sound.X_WAYS_EXPAND, Sound.X_WAYS_EXPAND);
                                Game.foregroundElements.featureLogoContainer.playXWaysActive();
                            } else {
                                Game.foregroundElements.featureLogoContainer.playXSplitActive();
                            }
                        }
                    ]);
                    // tl.to({},{duration : 1000});
                    break;
                case SYMBOL.X_NUDGE:
                    tl.add(symbolContainer.showEnhancerFeature(2, triggeredFeature), 0);
                    tl.add(Game.enhancerView.open(reelId), 0.2);
                    tl.add(() => {
                        Game.foregroundElements.featureLogoContainer.playXNudgeActive()
                    });
                    break;
                default:
                    tl.add([
                        Game.enhancerView.open(reelId),
                        symbolContainer.revealEnhancerSymbol(triggeredFeature, 1).add(() => {
                            //Game.stickyView.checkAndSetStickyDeadMultiplierToSymbol(symbolContainer);
                        })
                    ], 0.2);
            }
            tl.add(() => {
                Game.foregroundElements.waysCounter.updatePossibleWaysCounter();
            })
        }
        return tl;
    }

    private onSkip(tl: gsap.core.Timeline) {
        tl.timeScale(10);
    }
}